
import * as moment from 'moment';

import { Metadata } from '../models/metadata';
import { findUniqueVals } from '../services/util';

import { IPlotRange, IRange } from './range';

/** The type of dataset to plot */
export enum DatasetType {
    analog = 'ANALOG',
    discrete = 'DISCRETE',
    eventTable = 'EVENT_TABLE',
    image = 'IMAGE'
}

/** State types for limit violations */
export enum LimitTypes {
    BAD,
    WARN,
    GOOD
}

export interface IIndex {
    name?: string;
    value: number;
}

/** Model to dynamically access returned columns from LATIS */
export interface IIndexes {
    x: IIndex;
    y: IIndex;
    z?: IIndex;
    yMin: IIndex;
    yMax: IIndex;
}

export interface ILimitViolations {
    red: number;
    yellow: number;
}

export interface IDataset {
    name: string;
    indexes?: IIndexes;
    range?: IPlotRange;
    offset?: number | string;
    color?: string;
    type?: DatasetType;
    data?: [ number, number ][]; // [ [ x, y ] ]
    minMaxData?: [ number, number ][]; // [ [ x, yMin, yMax ] ]
    metadata?: Metadata;
    violations?: ILimitViolations;
    discreteValues?: any[];
    yAxisBreaks?;
    typeConversions?: any[];
    url: string;
    eventsUrl?: string;
    uid?: string;
    fullURL?: string;
    desc?: string;
    loaded?: boolean;
    isFullResolution?: boolean;
    minMaxSeriesIndex?: number;
}

export const DEFAULT_DATASET: IDataset = {
    offset: 0,
    color: '',
    type: DatasetType.analog,
    indexes: {
        x: {
            name: 'time',
            value: 0
        },
        y: {
            name: 'y',
            value: 1
        },
        yMin: { value: 2 },
        yMax: { value: 3 }
    },
    name: '',
    url: '',
    desc: '',
    loaded: false,
    minMaxSeriesIndex: null
};

/** returns the offset defined for this dataset */
export function parseOffset( offset: number | string ): number {
    if ( typeof offset === 'undefined' ) {
        return 0;
    } else if ( typeof offset === 'number' ) {
        return offset;
    } else {
        const scalar = parseFloat( offset.split(' ')[0] );
        const period = offset.split(' ')[1] as moment.unitOfTime.DurationConstructor;
        return moment.duration( scalar, period ).asMilliseconds();
    }
}

/** returns a string that contains both the name of the x variable and its units */
export function getXNameAndUnits( dataset: IDataset ): string {
    let returnVal = dataset.indexes.x.name;
    try {
        if ( typeof dataset.metadata.independentVariable.units !== 'undefined' ) {
            returnVal += ' (' + dataset.metadata.independentVariable.units + ')';
        }
    } catch ( e ) {
        // some part of this.metadata.Info was undefined. Just continue and return only the name.
    }
    return returnVal;
}

/** returns a string that contains both the name of the y variable and its units */
export function getYNameAndUnits( dataset: IDataset ): string {
    let returnVal = dataset.indexes.y.name;
    try {
        if ( typeof dataset.metadata.info.units !== 'undefined' ) {
            returnVal += ' (' + dataset.metadata.info.units + ')';
        }
    } catch ( e ) {
        // some part of this.metadata.Info was undefined. Just continue and return only the name.
    }
    return returnVal;
}

/** a bit of magic, but determines what type of plot we have; defaults to analog */
export function determineDatasetType( dataset: IDataset ) {
    // determine what kind of plot this is. Users aren't allowed to overlay multiple different kinds of datasets
    // on one plot, so the plot type is determined by only one of the datasets
    const url = dataset.url;
    if ( url.indexOf('Discrete') !== -1 ) {
        return DatasetType.discrete;
    } else if ( url.indexOf('String') !== -1 ) {
        return DatasetType.eventTable;
    } else if ( url.indexOf('image') !== -1 ) {
        return DatasetType.image;
    } else {
        return DatasetType.analog;
    }
}

/** uses the metadata to determine where yellow and red limit violation bands should appear on the plot */
export function getLimitZones( metadata: Metadata, greenColor: string, yellowColor: string, redColor: string ) {
    // create y-value color zones that highstock can understand
    let zones: { value?: number, color?: string }[] = [];
    const stateConversions = metadata.typeConversions;
    if ( metadata.limits.exist ) {
        zones = [ {
            // from -Infinity to Red.Low
            value: metadata.limits.red.low,
            color: redColor
        }, {
            // from Red.Low to Yellow.Low
            value: metadata.limits.yellow.low,
            color: yellowColor
        }, {
            // from Yellow.Low to Yellow.High
            value: metadata.limits.yellow.high,
            color: greenColor
        }, {
            // from Yellow.High to Red.High
            value: metadata.limits.red.high,
            color: yellowColor
        }, {
            // from Red.High to Infinity
            // no "value" means Infinity
            color: redColor
        } ];
    } else if ( typeof stateConversions !== 'undefined' ) {
        // state conversions are already sorted by ascending y-value
        const zoneWidth = 0.05;
        stateConversions.forEach( conversion => {
            const color = conversion.desirability === 'GOOD' ? greenColor :
                        conversion.desirability === 'CAUTION' ? yellowColor :
                        conversion.desirability === 'BAD' ? redColor :
                        undefined;
            if ( color !== undefined ) {
                // create a zone right around this single value
                zones.push({
                    // from the previous value to right below this conversion's value
                    value: conversion.value - zoneWidth
                    // no color means default series color
                });
                zones.push({
                    // from right below this conversion's value to right above it
                    value: conversion.value + zoneWidth,
                    color: color
                });
            }
        });
        zones.push({
            // from the previous zone definition to Infinity, use the default series color
            // (this is done by giving it an empty object with no defined color or value)
        });
    }

    return zones;
}

/**
 * Generate a new [x,y] data array from a given set of indexes
 * Raw data can have many columns in each row (i.e. [ x, z, min, y, max ])
 * So this allows us to pick out the plotable indices for highstock
 */
export function generateData( data: any[], indexes: IIndexes ): [ number, number ][] {
    // return data that looks like [x,y] for each point
    return data.map( point => [ point[ indexes.x.value ], point[ indexes.y.value ] ]);
}

/** Offset the raw data. This is done to put the dataset on the same x-axis and in the same range as the other datasets. */
export function offsetData( data: any[], indexes: IIndexes, offset: number ): any[] {
    return data.map( point => {
        point[ indexes.x.value ] -= offset;
        return point;
    });
}

/** returns a list of the unique values appearing in a dataset */
export function getUsedDiscreteVals( data: any[], indexes: IIndexes ): number[] {
    return findUniqueVals( data, element => element[ indexes.y.value ] );
}

/** return data that looks like [x,yMin,yMax] for each point */
export function generateMinMaxData( data: any[], indexes: IIndexes ): [ number, number, number ][] {
    return data.map( point => [ point[ indexes.x.value ], point[ indexes.yMin.value ], point[ indexes.yMax.value ] ] );
}

/** a data set is at full resolution if the ymin and ymax data are equal, or no yMin or yMax is set */
export function checkFullResolution( data: any[], indexes: IIndexes ): boolean {
    if ( indexes.yMin == null || indexes.yMax == null ) { return true; }

    return data.some( point => point[ indexes.yMin.value ] === point[ indexes.yMax.value ] );
}

/** returns the full range of the independent plot variable */
export function getXRange( data: any[], indexes: IIndexes ): IRange {
    if ( data.length === 0 ) { return { start: 0, end: 0 }; }

    return {
        start: data[ 0 ][ indexes.x.value ],
        end: data[ data.length - 1 ][ indexes.x.value ]
    };
}

/**
 * Generates a dataset with nulls inserted to create gaps
 * @param threshold The threshold determines how many empty cadence intervals it takes for two points to warrant a gap between them
 */
export function generateDataWithGaps( data: any[], indexes: IIndexes, threshold: number ): [ number, number ][] {

    if ( data.length < 4 ) {
        // if the array is this short, there's not enough info to calculate where gaps are
        return data;
    }

    // loop through the data array to find where gaps should be inserted, and insert null points into data and minMaxData
    // If the interval between two points is significantly greater than both of the adjacent intervals, then insert a null
    // point in that interval. End the loop 3 short of the end of the array, because we need to look at three intervals at
    // a time, which involves 4 points at a time (looking 3 points ahead).
    let interval1;
    let interval2;
    let interval3;

    const gapsData = [];

    for ( let i = 0; i < data.length - 3; i++ ) {
        if ( typeof interval1 === 'undefined' ) {
            interval1 = data[ i + 1 ][ indexes.x.value ] - data[i][ indexes.x.value ];
            interval2 = data[ i + 2 ][ indexes.x.value ] - data[ i + 1 ][ indexes.x.value ];
        } else {
            // reuse a couple of the intervals that we calculated last time
            // as we move one step to the right, intervals 2  and 3 from last time become intervals 1 and 2, respectively
            interval1 = interval2;
            interval2 = interval3;
        }
        interval3 = data[ i + 3 ][ indexes.x.value ] - data[ i + 2 ][ indexes.x.value ];

        // push the second point out of the four we're looking at onto the new arrays. we might insert a null between the
        // 2nd and 3rd points, so we don't want to insert the 3rd or 4th points yet (we will at the next loop iteration)
        gapsData.push( data[ i + 1 ] );

        // compare the center interval with its adjacent intervals.
        // In order for a gap to appear, the center interval must be larger than threshold times the largest of the adjacent intervals
        if ( interval2 > threshold * Math.max( interval1, interval3 ) ) {
            const midpointTimestamp = ( data[ i + 1 ][ indexes.x.value ] + data[ i + 2 ][ indexes.x.value ] ) / 2;
            gapsData.push( [ midpointTimestamp, null ] );
        }
    }

    // copy the first data point and last two data points to the new array, because they weren't inserted during the loop
    gapsData.unshift( data[0] );
    gapsData.push( data[ data.length - 2 ], data[ data.length - 1 ] );

    return gapsData;
}

/**
 * takes a line series and turns it into a min/max series,
 * with both the min and the max equalling the y-value of the line series.
 * Returns false if the data does not contain a line series, or if it already contains an arearange series.
 * Returns true on success.
 */
export function createMinMaxDataFromLine( data: any[], indexes: IIndexes ) {
    // alter the raw data, creating min/max data.
    return data.map( point => [
        point[ indexes.x.value ],
        point[ indexes.yMin.value ] = point[ indexes.y.value ],
        point[ indexes.yMax.value ] = point[ indexes.y.value ]
    ]);
}

/** Loops through the data to determine if any points fall outside the defined red/yellow limits */
export function checkLimitViolations( data: any[], indexes: IIndexes, metadata: Metadata ): ILimitViolations {
    const redLow = metadata.limits ? metadata.limits.red.low : undefined;
    const redHigh = metadata.limits ? metadata.limits.red.high : undefined;
    const yellowLow = metadata.limits ? metadata.limits.yellow.low : undefined;
    const yellowHigh = metadata.limits ? metadata.limits.yellow.high : undefined;

    const stateConversions = ( typeof metadata['State Conversions'] === 'undefined' ) ? null : metadata['State Conversions'];

    // for discrete datasets
    if ( stateConversions ) {
        return countLimitViolations( point => {
            // return a limit type based on if this data point violates any of the defined limits
            const stateConversion = stateConversions.find( conversion => {
                return conversion.value === point[ indexes.y.value ];
            });

            if ( !stateConversions ) {
                return; // no conversion available; return undefined.
            } else if ( stateConversions.desirability === 'BAD' ) {
                return LimitTypes.BAD;
            } else if ( stateConversions.desirability === 'CAUTION' ) {
                return LimitTypes.WARN;
            } else {
                return LimitTypes.GOOD;
            }
        });
    } else if ( redLow != null && redHigh != null && yellowLow != null && yellowHigh != null ) {
        return countLimitViolations( point => {
            const y = point[ indexes.y.value ];
            const yMin = indexes.yMin == null ? y : point[ indexes.yMin.value ];
            const yMax = indexes.yMax == null ? y : point[ indexes.yMax.value ];

            // return a limit type based on if this data point violates any of the defined limits
            if ( yMin > yellowLow && yMax < yellowHigh ) {
                return LimitTypes.GOOD;
            } else if ( (yMin <= yellowLow && yMin > redLow) || (yMax >= yellowHigh && yMax < redHigh) ) {
                return LimitTypes.WARN;
            } else if ( yMin <= redLow || yMax >= redHigh ) {
                return LimitTypes.BAD;
            }
        });
    }

    /** determines how many of red/yellow violations are contained within a dataset */
    function countLimitViolations( limitCheckFn: Function ): ILimitViolations {
        // count number of limit violations in a dataset.
        // limitCheckFn should take a data point as a parameter and return either LimitTypes.GOOD, LimitTypes.WARN, or LimitTypes.BAD
        //    based on whether that point violates any limits
        //    or return undefined if limit information is not available
        const numViolations: ILimitViolations = {
            yellow: 0,
            red: 0
        };
        const filteredData = data.filter( point => point[ indexes.y.value ] !== null );

        filteredData.forEach( point => {
            const limitState = limitCheckFn( point );
            switch ( limitState ) {
            case LimitTypes.WARN:
                numViolations.yellow++;
                break;
            case LimitTypes.BAD:
                numViolations.red++;
                break;
            default:
            }
        });
        return numViolations;
    }
}
